package org.gstechnovos.jexonplastech.sections.about.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.about.styles.CoreValuesContainerStyle
import org.gstechnovos.jexonplastech.sections.about.styles.ValueCardStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Images:
 * Value Icons: Hardcoded to render at exactly 64x64px with a 24px bottom margin.
 * Constraint: Strictly use a 1:1 square aspect ratio (SVG preferred, or 128x128px PNG for retina displays).
 *
 * Text:
 * Overline: Renders at 14px in uppercase. Limit to 2–3 words.
 * Headline: Renders at 36px on desktop and 28px on mobile. Limit to 4–7 words.
 * Value Titles: Renders at 20px. Limit to 2–3 words to prevent awkward text wrapping.
 * Value Descriptions: Renders at 16px. Limit to 2–3 sentences.
 *
 * Tip: Keep the character count roughly equal across all three values so the bottoms of the cards
 * align perfectly.
 *
 */
data class CoreValueItem(
    val title: String,
    val description: String,
    val iconPath: String
)

data class AboutCoreValuesData(
    val overline: String,
    val headline: String,
    val values: List<CoreValueItem>
)

val defaultAboutCoreValuesData = AboutCoreValuesData(
    overline = "OUR MISSION",
    headline = "The Core Values That Drive Us",
    values = listOf(
        CoreValueItem(
            title = "Uncompromising Quality",
            description = "We refuse to cut corners. Every product that leaves our facility undergoes strict quality control to ensure unmatched durability for the end consumer.",
            iconPath = Res.Image.About.Mission.QUALITY
        ),
        CoreValueItem(
            title = "B2B Partnerships",
            description = "We view our distributors and vendors as true partners. We prioritize transparent communication, reliable stock, and competitive bulk pricing.",
            iconPath = Res.Image.About.Mission.PARTNERSHIP
        ),
        CoreValueItem(
            title = "Material Safety",
            description = "We are committed to using 100% BPA-free, non-toxic, and food-grade plastics across our entire storage and utility catalog.",
            iconPath = Res.Image.About.Mission.SAFETY
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun AboutCoreValuesSection(data: AboutCoreValuesData = defaultAboutCoreValuesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // TRACK SCROLL VISIBILITY
    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Column(
        modifier = CoreValuesContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Column)
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
                .toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }
        ) {
            // --- SECTION HEADER ---
            Column(
                modifier = Modifier.maxWidth(800.px).margin(bottom = 60.px).animateSlideUp(isVisible, delayMs = 0),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = data.overline,
                    modifier = Modifier
                        .color(palette.brand.primaryBlue)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Bold)
                        .letterSpacing(1.5.px)
                        .textTransform(TextTransform.Uppercase)
                        .margin(bottom = 12.px)
                )

                SpanText(
                    text = data.headline,
                    modifier = Modifier
                        .color(palette.custom.darkText)
                        .fontSize(if (isDesktop) 36.px else 28.px)
                        .fontWeight(FontWeight.Bold)
                        .textAlign(TextAlign.Center)
                )
            }

            // --- CORE VALUES GRID ---
            SimpleGrid(
                // 1 column mobile, 3 columns desktop
                numColumns(1, md = 3),
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(30.px)
            ) {
                data.values.forEachIndexed { index, item ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(100.percent) // Stretch height for uniform cards
                            .animateSlideUp(isVisible, delayMs = 150 + (index * 150))
                    ) {
                        ValueCard(item)
                    }
                }
            }
        }
    }
}

@Composable
private fun ValueCard(item: CoreValueItem) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = ValueCardStyle.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Top
    ) {
        // Icon
        Image(
            src = item.iconPath,
            alt = item.title,
            modifier = Modifier
                .size(64.px)
                .margin(bottom = 24.px)
                .unextractable()
        )

        // Title
        SpanText(
            text = item.title,
            modifier = Modifier
                .color(palette.custom.darkText)
                .fontSize(20.px)
                .fontWeight(FontWeight.Bold)
                .margin(bottom = 16.px)
                .textAlign(TextAlign.Center)
        )

        // Description
        SpanText(
            text = item.description,
            modifier = Modifier
                .color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f))
                .fontSize(16.px)
                .lineHeight(1.6)
                .textAlign(TextAlign.Center)
        )
    }
}